import React, { useState } from 'react';
import { Layers, Plus, Trash2 } from 'lucide-react';
import type { CollectionField, FieldType } from '../types';

export interface CollectionSchemaBuilderProps {
  initialFields?: CollectionField[];
  onSave: (fields: CollectionField[]) => void;
}

const FIELD_TYPES: FieldType[] = ['RichText', 'Slug', 'MediaAsset', 'DateTime', 'BadgeTag'];

// CollectionSchemaBuilder allows visual definition of custom CMS fields and types.
export function CollectionSchemaBuilder({
  initialFields = [],
  onSave,
}: CollectionSchemaBuilderProps): React.JSX.Element {
  const [fields, setFields] = useState<CollectionField[]>(initialFields);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState<FieldType>('RichText');

  const addField = () => {
    if (!newFieldName.trim()) return;
    const updated = [...fields, { name: newFieldName.trim(), type: newFieldType, required: false }];
    setFields(updated);
    setNewFieldName('');
    onSave(updated);
  };

  const removeField = (index: number) => {
    const updated = fields.filter((_, i) => i !== index);
    setFields(updated);
    onSave(updated);
  };

  return (
    <div className="p-6 bg-card border border-border rounded-xl flex flex-col gap-6">
      <div className="flex items-center gap-2 border-b border-border pb-4">
        <Layers className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Collection Schema Builder</h2>
      </div>

      <div className="flex flex-col gap-3">
        {fields.map((f, i) => (
          <div
            key={`${f.name}-${i}`}
            className="flex items-center justify-between p-3 rounded-lg border border-border bg-background"
          >
            <div className="flex items-center gap-3">
              <span className="font-medium text-sm text-foreground">{f.name}</span>
              <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground font-mono">
                {f.type}
              </span>
            </div>
            <button
              type="button"
              onClick={() => removeField(i)}
              className="p-1 text-muted-foreground hover:text-primary transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Remove field"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      <div className="flex flex-col md:flex-row gap-3 pt-2">
        <input
          type="text"
          value={newFieldName}
          onChange={(e) => setNewFieldName(e.target.value)}
          placeholder="Field name (e.g. publication_date)"
          className="flex-1 text-base md:text-sm p-2 rounded border border-border bg-background text-foreground"
        />
        <select
          value={newFieldType}
          onChange={(e) => setNewFieldType(e.target.value as FieldType)}
          className="text-base md:text-sm p-2 rounded border border-border bg-background text-foreground"
        >
          {FIELD_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={addField}
          className="flex items-center justify-center gap-1.5 px-4 py-2 rounded bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 transition-opacity min-h-[44px]"
        >
          <Plus className="h-4 w-4" />
          <span>Add Field</span>
        </button>
      </div>
    </div>
  );
}
